import React, { type CSSProperties, forwardRef, useEffect, useState, type JSX } from 'react';
import type { ControlProps } from '../../index';
import { InputFieldLabelText, InputFieldLabel, Input } from './styled';

interface InputFieldProps extends ControlProps {
  name: string;
  value: string;
  label: string;
  placeholder?: string;
  className?: string;
  style?: CSSProperties;
  iconLeft?: JSX.Element;
  iconRight?: boolean;
  isValid?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (val: boolean) => void;
}

let timeOut: ReturnType<typeof setTimeout>;

type Ref = HTMLInputElement;

const InputField = forwardRef<Ref, InputFieldProps>((props, ref): JSX.Element => {
  const {
    name,
    placeholder,
    className,
    style,
    iconLeft,
    iconRight,
    label,
    isValid = true,
    onUpdate,
    onChange,
    onKeyDown,
    onFocus,
  } = props;
  const [value, setValue] = useState(props.value);
  const [focus, setFocus] = useState(false);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);

    if (e.target.value !== props.value && onUpdate) {
      clearTimeout(timeOut);
      timeOut = setTimeout(() => {
        onUpdate({ target: { name, value: e.target.value } });
      }, 300);
    }
    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = (val: boolean) => {
    setFocus(val);
    if (onFocus) {
      onFocus(val);
    }
  };

  useEffect(() => {
    if (props.value !== value) {
      setValue('');
    }
  }, [props.value]);

  return (
    <InputFieldLabel
      htmlFor={name}
      focus={focus}
      className={className}
      style={style}
      hasIconLeft={!!iconLeft}
      hasIconRight={!!iconRight}
      isValid={isValid}
    >
      {iconLeft && iconLeft}
      <InputFieldLabelText hasValue={!!value} focus={focus} isValid={isValid}>
        {label}
      </InputFieldLabelText>
      <Input
        {...(ref ? { ref } : {})}
        type="text"
        name={name}
        id={name}
        value={value}
        placeholder={placeholder}
        onChange={(e) => handleOnChange(e)}
        onKeyDown={(e) => onKeyDown && onKeyDown(e)}
        onFocus={() => handleFocus(true)}
        onBlur={() => handleFocus(false)}
      />
      {iconRight && iconRight}
    </InputFieldLabel>
  );
});

InputField.displayName = 'InputField';

export default InputField;
